export interface WallClockDateParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  hasTime: boolean;
}

const DATE_TIME_PARTS_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/;

const pad2 = (value: number) => String(value).padStart(2, '0');

const isValidDateParts = (year: number, month: number, day: number) => {
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

const partsFromDate = (date: Date): WallClockDateParts | null => {
  if (Number.isNaN(date.getTime())) return null;

  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds(),
    hasTime: true,
  };
};

export const getWallClockDateParts = (value: unknown): WallClockDateParts | null => {
  if (!value) return null;
  if (value instanceof Date) return partsFromDate(value);

  const raw = String(value).trim();
  const match = raw.match(DATE_TIME_PARTS_RE);

  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const hour = match[4] == null ? 0 : Number(match[4]);
    const minute = match[5] == null ? 0 : Number(match[5]);
    const second = match[6] == null ? 0 : Number(match[6]);

    if (
      isValidDateParts(year, month, day) &&
      hour >= 0 &&
      hour <= 23 &&
      minute >= 0 &&
      minute <= 59 &&
      second >= 0 &&
      second <= 59
    ) {
      return {
        year,
        month,
        day,
        hour,
        minute,
        second,
        hasTime: match[4] != null,
      };
    }
  }

  return partsFromDate(new Date(raw));
};

export const toWallClockDate = (value: unknown): Date | null => {
  const parts = getWallClockDateParts(value);
  if (!parts) return null;
  return new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
};

// The inverse of toWallClockDate: `date`'s local wall-clock time stamped as
// UTC, the convention stored display dates use (e.g. local 3:02 PM →
// "…T15:02:00.000Z"), so it compares directly with them.
export const toWallClockUtcIso = (date: Date = new Date()): string =>
  new Date(Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds()
  )).toISOString();

export const toWallClockDateInputValue = (value: unknown): string => {
  const parts = getWallClockDateParts(value);
  if (!parts) return '';
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
};

export const toWallClockTimeInputValue = (value: unknown): string => {
  const parts = getWallClockDateParts(value);
  if (!parts || !parts.hasTime) return '';
  return `${pad2(parts.hour)}:${pad2(parts.minute)}`;
};

export const toHebrewPickerDateInputValue = (value: unknown): string => {
  const gregorianDate =
    value &&
    typeof value === 'object' &&
    typeof (value as { greg?: unknown }).greg === 'function'
      ? (value as { greg: () => Date }).greg()
      : value;

  return toWallClockDateInputValue(gregorianDate);
};
